import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faBoxArchive,
  faBoxOpen,
  faLink,
  faCheck,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";

function ShoppingListDetail({
  list,
  setShoppingLists,
  handleArchiveList,
  handleUnarchiveList,
  handleDeleteList,
  itemSuggestions = [],
  onItemAdded,
}) {
  const { t } = useTranslation();
  const [listName, setListName] = useState(list.name);
  const [items, setItems] = useState(list.items);
  const [newItemName, setNewItemName] = useState("");
  const [newItemPrice, setNewItemPrice] = useState("");
  const [newItemCurrency, setNewItemCurrency] = useState("UAH");
  const [itemNameError, setItemNameError] = useState(false);
  const [focusedItemId, setFocusedItemId] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);
  const isArchived = !!handleUnarchiveList;

  // Sync local state when list prop changes (from socket updates)
  useEffect(() => {
    setItems(list.items);
    setListName(list.name);
  }, [list.items, list.name]);

  // Filter suggestions based on input
  useEffect(() => {
    if (newItemName.trim()) {
      const filtered = itemSuggestions.filter(
        (suggestion) =>
          suggestion.toLowerCase().includes(newItemName.toLowerCase()) &&
          suggestion.toLowerCase() !== newItemName.toLowerCase()
      );
      setFilteredSuggestions(filtered.slice(0, 8)); // Limit to 8 suggestions
      setSelectedSuggestionIndex(-1);
    } else {
      setFilteredSuggestions([]);
    }
  }, [newItemName, itemSuggestions]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCopyLink = () => {
    const url = `${window.location.origin}/shopping-list/${list._id}`;
    navigator.clipboard.writeText(url).then(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    });
  };

  const handleUpdateListName = async (newName) => {
    try {
      const response = await axios.put(`/api/shopping-lists/${list._id}`, {
        name: newName,
      });
      setShoppingLists((prev) =>
        prev.map((l) => (l._id === list._id ? response.data : l))
      );
    } catch (error) {
      console.error("Error updating list name:", error);
    }
  };

  const handleAddItem = async (e) => {
    if (e) e.preventDefault();
    if (isArchived) return;
    if (!newItemName.trim()) {
      setItemNameError(true);
      setTimeout(() => setItemNameError(false), 2000);
      return;
    }
    setItemNameError(false);
    setShowSuggestions(false);
    const itemData = { name: newItemName };
    if (newItemPrice) {
      itemData.price = parseFloat(newItemPrice);
    }
    if (newItemCurrency) {
      itemData.currency = newItemCurrency;
    }
    try {
      const response = await axios.post(
        `/api/shopping-lists/${list._id}/items`,
        itemData
      );
      setItems(response.data.items);
      setNewItemName("");
      setNewItemPrice("");
      setNewItemCurrency("UAH");
      setShoppingLists((prev) =>
        prev.map((l) => (l._id === list._id ? response.data : l))
      );
      // Refetch suggestions to include the new item
      if (onItemAdded) onItemAdded();
      // Refocus on the input for quick consecutive additions
      if (inputRef.current) inputRef.current.focus();
    } catch (error) {
      console.error("Error adding item:", error);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setNewItemName(suggestion);
    setShowSuggestions(false);
    setFilteredSuggestions([]);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (
        showSuggestions &&
        selectedSuggestionIndex >= 0 &&
        filteredSuggestions[selectedSuggestionIndex]
      ) {
        handleSelectSuggestion(filteredSuggestions[selectedSuggestionIndex]);
      } else {
        handleAddItem();
      }
    } else if (
      e.key === "ArrowDown" &&
      showSuggestions &&
      filteredSuggestions.length > 0
    ) {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (
      e.key === "ArrowUp" &&
      showSuggestions &&
      filteredSuggestions.length > 0
    ) {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  const handleUpdateItem = async (itemId, updates) => {
    if (isArchived) return;
    try {
      const response = await axios.put(
        `/api/shopping-lists/${list._id}/items/${itemId}`,
        updates
      );
      setItems(response.data.items);
      setShoppingLists((prev) =>
        prev.map((l) => (l._id === list._id ? response.data : l))
      );
    } catch (error) {
      console.error("Error updating item:", error);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (isArchived) return;
    try {
      const response = await axios.delete(
        `/api/shopping-lists/${list._id}/items/${itemId}`
      );
      setItems(response.data.items);
      setShoppingLists((prev) =>
        prev.map((l) => (l._id === list._id ? response.data : l))
      );
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  return (
    <div
      className={`w-full relative ${
        showSuggestions && filteredSuggestions.length > 0 ? "z-[100]" : "z-10"
      }`}
    >
      <div
        className={`flex items-center justify-between gap-3 ${
          isArchived && items.length === 0 ? "mb-0" : "mb-4"
        }`}
      >
        <input
          type="text"
          value={listName}
          onChange={(e) => setListName(e.target.value)}
          onBlur={() => handleUpdateListName(listName)}
          disabled={isArchived}
          className={`text-xl font-bold flex-1 min-w-0 bg-transparent border-none outline-none focus:ring-0 ${
            isArchived ? "text-gray-500" : "text-gray-800"
          }`}
        />
        <div className="flex items-center gap-1 flex-shrink-0">
          {handleArchiveList && (
            <>
              <button
                onClick={handleCopyLink}
                className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 active:bg-primary-100 dark:active:bg-primary-900/30"
                title="Copy Link"
              >
                <FontAwesomeIcon
                  icon={isCopied ? faCheck : faLink}
                  className={isCopied ? "text-green-500" : ""}
                />
              </button>
              <button
                onClick={() => handleArchiveList(list._id)}
                className="p-2 text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors rounded-lg hover:bg-yellow-50 dark:hover:bg-yellow-900/20 active:bg-yellow-100 dark:active:bg-yellow-900/30"
                title="Archive"
              >
                <FontAwesomeIcon icon={faBoxArchive} />
              </button>
            </>
          )}
          {handleUnarchiveList && (
            <button
              onClick={() => handleUnarchiveList(list._id)}
              className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 active:bg-primary-100 dark:active:bg-primary-900/30"
              title="Unarchive"
            >
              <FontAwesomeIcon icon={faBoxOpen} />
            </button>
          )}
          {handleDeleteList && (
            <button
              onClick={() => handleDeleteList(list._id)}
              className="p-2 text-gray-400 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50"
              title="Delete"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </div>
      </div>

      <ul className="space-y-1.5">
        <AnimatePresence initial={false}>
          {items.map((item) => (
            <motion.li
              key={item._id}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div
                className={`flex items-center w-full rounded-lg transition-colors ${
                  item.completed
                    ? "bg-gray-50 dark:bg-gray-800"
                    : "hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                <div className="relative flex items-center justify-center w-5 h-5 mr-2 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={item.completed}
                    onChange={(e) =>
                      handleUpdateItem(item._id, {
                        completed: e.target.checked,
                      })
                    }
                    disabled={isArchived}
                    className="peer appearance-none w-4 h-4 border-2 border-gray-300 rounded-md checked:bg-primary-500 checked:border-primary-500 transition-colors cursor-pointer"
                  />
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="absolute text-white text-xs opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity"
                  />
                </div>

                {isArchived ? (
                  <div className="flex items-center flex-grow min-w-0">
                    <span
                      className={`truncate ${
                        item.completed
                          ? "line-through text-gray-400 dark:text-gray-500"
                          : "text-gray-700 dark:text-gray-200"
                      }`}
                    >
                      {item.name}
                    </span>
                    {item.price && (
                      <span className="ml-auto text-sm text-gray-500 whitespace-nowrap pl-2">
                        {item.price} {item.currency || "UAH"}
                      </span>
                    )}
                  </div>
                ) : (
                  <div
                    className="flex items-center flex-grow min-w-0 gap-2"
                    onBlur={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        handleUpdateItem(item._id, { name: item.name });
                        setFocusedItemId(null);
                      }
                    }}
                  >
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => {
                        setItems((prevItems) =>
                          prevItems.map((i) =>
                            i._id === item._id
                              ? { ...i, name: e.target.value }
                              : i
                          )
                        );
                      }}
                      onFocus={() => setFocusedItemId(item._id)}
                      onBlur={(e) => {
                        handleUpdateItem(item._id, { name: e.target.value });
                      }}
                      className={`flex-grow bg-transparent border-none outline-none min-w-0 text-sm dark:text-white ${
                        item.completed
                          ? "line-through text-gray-400 dark:text-gray-500"
                          : "text-gray-800 dark:text-white"
                      }`}
                    />

                    {focusedItemId === item._id ? (
                      <div className="flex items-center gap-1 animate-in fade-in duration-200">
                        <input
                          type="number"
                          step="0.01"
                          value={item.price || ""}
                          onChange={(e) => {
                            setItems((prevItems) =>
                              prevItems.map((i) =>
                                i._id === item._id
                                  ? { ...i, price: e.target.value }
                                  : i
                              )
                            );
                          }}
                          onBlur={(e) => {
                            const price = e.target.value
                              ? parseFloat(e.target.value)
                              : null;
                            handleUpdateItem(item._id, { price });
                          }}
                          placeholder={t("price")}
                          className="w-16 p-1 bg-white border border-gray-200 rounded text-xs"
                        />
                        <select
                          value={item.currency || "UAH"}
                          onChange={(e) => {
                            setItems((prevItems) =>
                              prevItems.map((i) =>
                                i._id === item._id
                                  ? { ...i, currency: e.target.value }
                                  : i
                              )
                            );
                            handleUpdateItem(item._id, {
                              currency: e.target.value,
                            });
                          }}
                          className="p-1 bg-white border border-gray-200 rounded text-xs"
                        >
                          <option value="UAH">UAH</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="HUF">HUF</option>
                        </select>
                      </div>
                    ) : (
                      item.price && (
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {item.price} {item.currency || "UAH"}
                        </span>
                      )
                    )}
                  </div>
                )}

                {!isArchived && (
                  <button
                    onClick={() => handleDeleteItem(item._id)}
                    className="ml-2 p-1 text-gray-300 hover:text-red-500 transition-colors"
                  >
                    <FontAwesomeIcon icon={faTrash} size="sm" />
                  </button>
                )}
              </div>
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>

      {!isArchived && (
        <form
          onSubmit={handleAddItem}
          className="mt-4 flex flex-wrap sm:flex-nowrap items-center gap-2"
        >
          <div className="flex-grow w-full sm:w-auto relative">
            <div className="flex items-center bg-gray-50 dark:bg-dark-surface rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-primary-100 transition-all">
              <FontAwesomeIcon
                icon={faPlus}
                className="text-gray-400 mr-2"
                size="sm"
              />
              <input
                ref={inputRef}
                type="text"
                value={newItemName}
                onChange={(e) => {
                  setNewItemName(e.target.value);
                  if (itemNameError) setItemNameError(false);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={handleInputKeyDown}
                placeholder={t("addNewItem")}
                enterKeyHint="done"
                autoComplete="off"
                className={`bg-transparent border-none outline-none w-full text-sm dark:text-white ${
                  itemNameError
                    ? "placeholder-red-400"
                    : "placeholder-gray-400 dark:placeholder-gray-500"
                }`}
              />
            </div>

            {/* Autocomplete Suggestions Dropdown */}
            <AnimatePresence>
              {showSuggestions && filteredSuggestions.length > 0 && (
                <motion.div
                  ref={suggestionsRef}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-dark-surface border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[100] overflow-hidden"
                >
                  {filteredSuggestions.map((suggestion, index) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => handleSelectSuggestion(suggestion)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        index === selectedSuggestionIndex
                          ? "bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                          : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      {suggestion}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex items-center gap-1 w-full sm:w-auto justify-end">
            <input
              type="number"
              step="0.01"
              value={newItemPrice}
              onChange={(e) => setNewItemPrice(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddItem();
                }
              }}
              placeholder={t("price")}
              enterKeyHint="done"
              className="w-20 p-2 bg-gray-50 dark:bg-dark-surface border-none rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-100 dark:text-gray-200 transition-colors"
            />
            <select
              value={newItemCurrency}
              onChange={(e) => setNewItemCurrency(e.target.value)}
              className="p-2 bg-gray-50 dark:bg-dark-surface border-none rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-100 dark:text-gray-200 transition-colors"
            >
              <option value="UAH">UAH</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="HUF">HUF</option>
            </select>
            <button
              type="submit"
              className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm flex-shrink-0"
            >
              <FontAwesomeIcon icon={faPlus} size="sm" />
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default ShoppingListDetail;
